'use client'

import type { PointOfInterestImage } from 'landings/Home/Home.type'
import { getImageProps } from 'next/image'
import type { CSSProperties } from 'react'
import { pointOfInterestImageLoaderFactory } from 'utils/image/loaders/pointOfInterestImageLoaderFactory'
import { getPointOfInterest } from '../../../utils/getPointOfInterest/getPointOfInterest'
import styles from './FamilyBannerShopImage.module.css'

type FamilyBannerShopImageProps = {
	readonly image: PointOfInterestImage
	readonly priority?: boolean
}

type FetchPriority = 'low' | 'high'

const ASPECT_RATIO_PORTRAIT_WIDTH = 5
const ASPECT_RATIO_PORTRAIT_HEIGHT = 7
const ASPECT_RATIO_PORTRAIT =
	ASPECT_RATIO_PORTRAIT_WIDTH / ASPECT_RATIO_PORTRAIT_HEIGHT

const ASPECT_RATIO_SQUARE = 1
const HALF = 0.5

export function FamilyBannerShopImage({
	image,
	priority = false,
}: FamilyBannerShopImageProps) {
	const pointOfInterest = getPointOfInterest({ image })
	const portraitLoader = pointOfInterestImageLoaderFactory(
		pointOfInterest,
		(width) => width / ASPECT_RATIO_PORTRAIT,
		(width) => width * HALF
	)
	const squareLoader = pointOfInterestImageLoaderFactory(
		pointOfInterest,
		(width) => width / ASPECT_RATIO_SQUARE,
		(width) => width * HALF
	)

	const commonImageProps = {
		priority,
		alt: image.description ?? '',
		fetchPriority: (priority ? 'high' : 'low') as FetchPriority,
		src: image.url,
		'aria-hidden': true,
		fill: true,
	}

	const {
		props: { srcSet: portraitSrcSet },
	} = getImageProps({
		...commonImageProps,
		loader: portraitLoader,
	})
	const {
		props: { srcSet: squareSrcSet, ...restImageProps },
	} = getImageProps({
		...commonImageProps,
		loader: squareLoader,
	})

	return (
		<picture>
			<source media='(min-width: 1024px)' srcSet={squareSrcSet} />
			<source media='(max-width: 1023px)' srcSet={portraitSrcSet} />
			<img
				{...restImageProps}
				alt={image.description ?? ''}
				style={{ ...restImageProps.style } as CSSProperties}
				className={styles.image}
				draggable='false'
			/>
		</picture>
	)
}
